
import { useState } from 'react';
import { useToast } from './use-toast';
import { PostgrestSingleResponse, PostgrestResponse, PostgrestError } from '@supabase/supabase-js';

export function useSupabaseOperation() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const executeOperation = async <T,>(
    operation: () => Promise<PostgrestSingleResponse<T> | PostgrestResponse<T>>,
    {
      loadingMessage = 'Processing...',
      successMessage = 'Operation completed successfully.',
      errorMessage = 'An error occurred.',
      showToasts = false, // Changed default to false
    } = {}
  ): Promise<T | null> => {
    setLoading(true);
    
    try {
      const { data, error } = await operation();
      
      if (error) {
        if (showToasts) {
          toast({
            title: 'Error',
            description: error.message || errorMessage,
            variant: 'destructive',
          });
        }
        logger.error('Supabase operation error:', error);
        return null;
      }
      
      // Handle both array and single result cases
      if (data === null) {
        return null;
      }
      
      if (showToasts) {
        toast({
          title: 'Success',
          description: successMessage,
        });
      }
      
      return data as T;
    } catch (err) {
      logger.error('Unexpected error during Supabase operation:', err);
      if (showToasts) {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    executeOperation,
  };
}
